import slsLogger from '@/utils/slsLogger';

const WORKBUDDY_JSAPI_URL = 'https://openplatform-cdn.codebuddy.cn/open/sdk/jsapi/0.0.1/jsapi.js';
const WORKBUDDY_JSAPI_SCRIPT_ID = 'workbuddy-jsapi';

const hasWorkBuddyLoginApi = () => typeof window !== 'undefined' && typeof window.wb?.login === 'function';

let workBuddyJsApiLoadPromise;

/**
 * 加载 WorkBuddy JSAPI，并确认 wb.login 已注册。
 * @returns {Promise<boolean>} JSAPI 是否加载成功
 */
export const loadWorkBuddyJsApi = () => {
  if (hasWorkBuddyLoginApi()) return Promise.resolve(true);
  if (workBuddyJsApiLoadPromise) return workBuddyJsApiLoadPromise;

  workBuddyJsApiLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = WORKBUDDY_JSAPI_SCRIPT_ID;
    script.src = WORKBUDDY_JSAPI_URL;
    script.async = true;
    script.onload = () => resolve(hasWorkBuddyLoginApi());
    script.onerror = () => reject(new Error('WorkBuddy JSAPI 加载失败'));
    document.head.appendChild(script);
  });

  return workBuddyJsApiLoadPromise;
};

/**
 * 调用 wb.login 取一次 WorkBuddy 授权码。
 *
 * 不传任何入参，由客户端按当前授权状态决定是直接返回 code 还是唤起授权弹窗。
 * 只要返回里带 code 就采用，不额外要求 ok 为真，避免客户端省略该字段时把有效 code 丢掉。
 *
 * code 是一次性凭证，用过即失效，每个需要识别 WorkBuddy 身份的操作都要单独取一次。
 * @param {string} logComponent - slsLogger 组件名，用于区分调用来源
 * @returns {Promise<string>} 已授权时返回 code，JSAPI 不可用或用户未授权时返回空串
 * @example
 * const code = await requestWorkBuddyAuthorizationCode('WorkBuddyUnbind');
 * if (code) await unbindWorkBuddyAccount(code);
 */
export const requestWorkBuddyAuthorizationCode = async (logComponent) => {
  let hasLoginApi = false;
  try {
    hasLoginApi = await loadWorkBuddyJsApi();
  } catch (error) {
    slsLogger.console({
      component: logComponent,
      level: 'error',
      message: 'WorkBuddy 取授权码-JSAPI 加载失败',
      extra: { errorMessage: error?.message, ...error }
    });
    return '';
  }
  if (!hasLoginApi) return '';

  let result;
  try {
    result = await window.wb.login();
  } catch (error) {
    slsLogger.console({
      component: logComponent,
      level: 'error',
      message: 'WorkBuddy 取授权码-wb.login 调用失败',
      extra: { errorMessage: error?.message, ...error, result }
    });
    return '';
  }

  slsLogger.console({
    component: logComponent,
    message: 'WorkBuddy 取授权码-wb.login 返回结果',
    extra: { ...result }
  });
  return result?.code || '';
};
